import pluralize from 'pluralize';

import {
  UTILITY_ACTIVATION_STATUS_CANCELED,
  UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED,
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  UTILITY_ACTIVATION_UI_STATE_PENDING,
  UTILITY_ACTIVATION_UI_STATE_WARNING,
  UTILITY_ACTIVATION_UI_STATE_COMPLETED,
  UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED,
  UTILITY_ACTIVATION_UI_STATE_TO_COLOR_MAPPING,
  UTILITY_ACTIVATION_UI_STATE_TO_ICON_MAPPING,
  UTILITY_ACTIVATION_FEEDBACK_TAGS_LOW_RATING,
  UTILITY_ACTIVATION_FEEDBACK_TAGS_HIGH_RATING,
  UTILITY_PARTNER_CUSTOMER_TYPE_DIRECT_TO_CONSUMER,
  UTILITY_PARTNER_CUSTOMER_TYPE_HOMEBUYER,
  UTILITY_PARTNER_CUSTOMER_TYPE_PROPERTY_MANAGER,
  UTILITY_PARTNER_DEFAULT_HOUSING_TYPE_HOMEOWNER,
  UTILITY_PARTNER_DEFAULT_HOUSING_TYPE_RENTER,
  UTILITY_ACTIVATION_ITEM_RENTER_DROPOUT_REASON_LIST,
  UTILITY_TYPE_ID_ELECTRICITY,
  UTILITY_PROVIDER_BLOCKED_MANUAL_MAPPING_IDS,
  UTILITY_PROVIDER_BLOCKED_MANUAL_MAPPING_NAMES,
} from 'common/utility/constants';

import { hasRoleAdmin, UTILITY_USER_STATUS_ACTIVE } from 'common/utility/userRoles';
import { USER_ROLE_RENTER } from 'common/userRoles';

export const getRenterUtilityLink = ({ partnerSlug, shortId }) => {
  return `/utility-setup/${partnerSlug}/${shortId}`;
};

export const getProviderPlanPriceText = ({ price, unit }) => {
  return `$${parseFloat(price).toFixed(2)}${unit ? `/${unit}` : ''}`;
};

export const getSupportSmsBody = ({ addressStreetDisplay }) => {
  if (global.encodeURIComponent) {
    const text = `Hi, I have a question about the utility setup for ${addressStreetDisplay}`;
    return global.encodeURIComponent(text);
  }
};

export const getActivationItemStatusUiState = ({
  status,
  defaultState = UTILITY_ACTIVATION_UI_STATE_PENDING,
  completedStates = [
    UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
    UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  ],
  warningStates = [UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED],
  label: initialLabel,
  utilityProviders = [],
}) => {
  const uiState = !status
    ? UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED
    : completedStates.includes(status)
    ? UTILITY_ACTIVATION_UI_STATE_COMPLETED
    : warningStates.includes(status)
    ? UTILITY_ACTIVATION_UI_STATE_WARNING
    : defaultState;

  const label = `${initialLabel}${
    !status
      ? ` (not required) ${
          utilityProviders.length > 0
            ? `${pluralize('provider', utilityProviders.length, true)} mapped`
            : ''
        }`
      : status === UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED
      ? ' (PURCHASE REQUESTED)'
      : ''
  }`;

  return {
    color: UTILITY_ACTIVATION_UI_STATE_TO_COLOR_MAPPING[uiState],
    iconUrl: UTILITY_ACTIVATION_UI_STATE_TO_ICON_MAPPING[uiState],
    label,
    uiState,
  };
};

export const getEntityOwnershipText = ({ name, word }) => {
  const nameWithApostrophe = name.endsWith('s') ? `${name}'` : `${name}'s`;
  return `${nameWithApostrophe} ${word}`;
};

export const isUtilityActivationActive = (utilityActivation) => {
  if (!utilityActivation?.status) {
    return false;
  }

  return (
    utilityActivation.status !== UTILITY_ACTIVATION_STATUS_CANCELED && !utilityActivation.archivedAt
  );
};

export const getUtilityActivationFeedbackTags = (rating) => {
  return rating < 5
    ? UTILITY_ACTIVATION_FEEDBACK_TAGS_LOW_RATING
    : UTILITY_ACTIVATION_FEEDBACK_TAGS_HIGH_RATING;
};

export const isEmailValid = (email) => {
  if (!email) {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const canAccessActivation = ({ utilityActivation, utilityUser }) => {
  const isActivationClaimed = utilityActivation.user?.status === UTILITY_USER_STATUS_ACTIVE;
  const isAdminUser = hasRoleAdmin(utilityUser.roles);

  if (!isActivationClaimed) {
    return true;
  }

  if (isAdminUser) {
    return true;
  }

  return utilityActivation.user.id === utilityUser.id;
};

const getPartnerEffectiveCustomerType = (utilityPartner) => {
  if (utilityPartner?.customerType !== UTILITY_PARTNER_CUSTOMER_TYPE_DIRECT_TO_CONSUMER) {
    return utilityPartner?.customerType;
  }
  if (utilityPartner.defaultHousingType === UTILITY_PARTNER_DEFAULT_HOUSING_TYPE_HOMEOWNER) {
    return UTILITY_PARTNER_CUSTOMER_TYPE_HOMEBUYER;
  }
  if (utilityPartner.defaultHousingType === UTILITY_PARTNER_DEFAULT_HOUSING_TYPE_RENTER) {
    return UTILITY_PARTNER_CUSTOMER_TYPE_PROPERTY_MANAGER;
  }
  return UTILITY_PARTNER_CUSTOMER_TYPE_DIRECT_TO_CONSUMER;
};

export const isHomebuyerPartner = (utilityPartner) =>
  getPartnerEffectiveCustomerType(utilityPartner) === UTILITY_PARTNER_CUSTOMER_TYPE_HOMEBUYER;

export const PARTNER_PLATFORM_UI_VARIANT_HOMEBUYER = 'homebuyer';
export const PARTNER_PLATFORM_UI_VARIANT_PROPERTY_MANAGER = 'property-manager';
export const PARTNER_PLATFORM_UI_VARIANT_DIRECT_TO_CONSUMER_DEFAULT =
  'direct-to-consumer-default';

export const getPartnerPlatformUiVariant = (utilityPartner) => {
  const effective = getPartnerEffectiveCustomerType(utilityPartner);
  if (effective === UTILITY_PARTNER_CUSTOMER_TYPE_HOMEBUYER) {
    return PARTNER_PLATFORM_UI_VARIANT_HOMEBUYER;
  }
  if (effective === UTILITY_PARTNER_CUSTOMER_TYPE_DIRECT_TO_CONSUMER) {
    return PARTNER_PLATFORM_UI_VARIANT_DIRECT_TO_CONSUMER_DEFAULT;
  }
  return PARTNER_PLATFORM_UI_VARIANT_PROPERTY_MANAGER;
};

export const buildPartnerPlatformUiVariantPayload = (utilityPartner) => ({
  partnerId: utilityPartner?.id ?? null,
  customerType: utilityPartner?.customerType ?? null,
  defaultHousingType: utilityPartner?.defaultHousingType ?? null,
  uiVariant: getPartnerPlatformUiVariant(utilityPartner),
  surface: 'partner-admin',
});

export const shouldViewTenantLiabilityBanners = (utilityPartner) =>
  utilityPartner?.experienceConfiguration?.tenantLiabilityBanner === true &&
  utilityPartner?.experienceConfiguration?.liabilityInsuranceEnabled !== true &&
  utilityPartner?.customerType === UTILITY_PARTNER_CUSTOMER_TYPE_PROPERTY_MANAGER;

/**
 * Determines if a dropout was initiated by the renter (or renter-intended)
 * vs admin/partner (order archived - needs reset).
 */
export const isUtilityItemDropoutByRenter = (utilityItem) => {
  if (utilityItem.status !== UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT) {
    return false;
  }

  if (utilityItem.archivedByUser) {
    return (utilityItem.archivedByUser.roles ?? []).includes(USER_ROLE_RENTER);
  }

  return UTILITY_ACTIVATION_ITEM_RENTER_DROPOUT_REASON_LIST.includes(utilityItem.dropoutReason);
};

export const getManualMappingBlockedProviders = ({
  utilityTypeId,
  currentProviderIds,
  newProviderIds,
}) => {
  if (utilityTypeId !== UTILITY_TYPE_ID_ELECTRICITY) {
    return [];
  }

  return UTILITY_PROVIDER_BLOCKED_MANUAL_MAPPING_IDS.filter((id) => {
    const wasPresent = currentProviderIds.includes(id);
    const willBePresent = newProviderIds.includes(id);
    return wasPresent !== willBePresent;
  });
};

export const getBlockedProviderLabel = (blockedProviderIds) => {
  return blockedProviderIds
    .map((id) => UTILITY_PROVIDER_BLOCKED_MANUAL_MAPPING_NAMES[id])
    .filter(Boolean)
    .join(' and ');
};

export const getBlockedProviderTitle = (blockedProviderIds, blockedProviderActions) => {
  const label = getBlockedProviderLabel(blockedProviderIds);
  const actionValues = blockedProviderIds.map((id) => blockedProviderActions[id]);
  const action = actionValues.every((a) => a === actionValues[0]) ? actionValues[0] : 'Mapping/Unmapping';
  return `${action} ${label}`;
};
